/** 주석 검증 결과 - 우측 지적 목록. 연결 → 별도 → 구분 미상으로 묶는다. */

import type { RefObject } from "react";

import type {
  IxdNoteChecklistFinding,
  IxdNoteChecklistLocation,
} from "@/apis/ixd-editor/types";
import { cn } from "@/lib/utils";

import { CS_TYPE_LABEL, findingLocationShape } from "./finding-location";

const CS_TYPE_ORDER: IxdNoteChecklistFinding["csType"][] = [
  "Consolidated",
  "Separated",
  "",
];

interface FindingsPanelProps {
  findings: IxdNoteChecklistFinding[];
  selectedIndex: number | null;
  /** locationIndex 가 없으면 첫 위치 */
  onSelect: (
    index: number,
    finding: IxdNoteChecklistFinding,
    locationIndex?: number,
  ) => void;
  describeLocation: (location: IxdNoteChecklistLocation) => string;
  /** 미리보기에서 고른 지적으로 목록을 스크롤할 때 `[data-finding-index]` 를 찾는 범위 */
  listRef: RefObject<HTMLDivElement>;
}

export const FindingsPanel = ({
  findings,
  selectedIndex,
  onSelect,
  describeLocation,
  listRef,
}: FindingsPanelProps) => {
  const indexed = findings.map((finding, index) => ({ finding, index }));

  return (
    <aside className="flex w-96 shrink-0 flex-col border-l border-line-normal bg-background-gray-normal">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line-normal bg-white px-5">
        <span className="label-1-normal-medium text-label-normal">
          주석 검토사항
        </span>
        <span className="caption-1-semibold text-label-alternative">
          {findings.length}건
        </span>
      </header>
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {findings.length === 0 && (
          <p className="p-5 caption-1-semibold text-label-alternative">
            검토사항이 없습니다.
          </p>
        )}
        {CS_TYPE_ORDER.map((csType) => {
          const group = indexed.filter((x) => x.finding.csType === csType);
          if (group.length === 0) return null;
          return (
            <section key={csType} className="border-b border-line-normal">
              <h2 className="border-b border-line-normal px-4 py-2 caption-1-semibold text-label-normal">
                {CS_TYPE_LABEL[csType]} ({group.length})
              </h2>
              <ul className="flex flex-col gap-2 p-2">
                {group.map(({ finding, index }) => (
                  <li key={index} data-finding-index={index}>
                    <FindingCard
                      finding={finding}
                      isSelected={index === selectedIndex}
                      onSelect={(locationIndex) =>
                        onSelect(index, finding, locationIndex)
                      }
                      describeLocation={describeLocation}
                    />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </aside>
  );
};

const SHAPE_NOTE = {
  whole: "주석 전체",
  unresolved: "위치 미확인",
} as const;

/**
 * 지적 카드. 위치가 있으면 위치마다 버튼을 달아, 한 지적이 여러 목차·표에 걸친 것을 보이고 각각으로 이동하게 한다.
 * 카드 본문을 누르면 첫 위치로 간다.
 */
const FindingCard = ({
  finding,
  isSelected,
  onSelect,
  describeLocation,
}: {
  finding: IxdNoteChecklistFinding;
  isSelected: boolean;
  onSelect: (locationIndex?: number) => void;
  describeLocation: (location: IxdNoteChecklistLocation) => string;
}) => {
  const shape = findingLocationShape(finding);
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="caption-1-semibold text-label-normal">
          {finding.checkName}
        </span>
        {shape !== "located" && (
          <span className="shrink-0 caption-2-medium text-label-alternative">
            {SHAPE_NOTE[shape]}
          </span>
        )}
      </div>
      {/* 백엔드 위치 문구는 원본 리포트처럼 앞 2곳만 담는다. 위치 목록이 있으면 그쪽이 전체라 문구는 뺀다. */}
      {shape !== "located" && (
        <p className="caption-2-medium text-label-alternative break-all">
          {finding.locationLabel}
        </p>
      )}
      <p className="caption-2-medium text-label-normal break-all">
        {finding.target}
      </p>
      <p className="caption-2-medium text-status-negative leading-relaxed">
        {finding.message}
      </p>
    </>
  );
  // 안내 문구에 띄어쓰기 없는 긴 태그명이 들어온다(M7 축 이름 72자 등). 카드 전체에서 긴 단어를 끊어
  // 카드가 패널 밖으로 삐져나가 목록이 가로로 밀리지 않게 한다.
  const className = cn(
    "flex w-full flex-col gap-1.5 break-words rounded-lg border bg-white p-3 text-left",
    isSelected ? "border-status-negative" : "border-line-normal",
  );

  if (shape !== "located") return <div className={className}>{body}</div>;
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => onSelect()}
        className="flex cursor-pointer flex-col gap-1.5 text-left"
      >
        {body}
      </button>
      <div className="flex flex-col gap-1 border-t border-line-normal pt-2">
        <span className="caption-2-medium text-label-alternative">
          위치 {finding.locations.length}곳
        </span>
        {finding.locations.map((location, locationIndex) => (
          <button
            key={locationIndex}
            type="button"
            onClick={() => onSelect(locationIndex)}
            className="cursor-pointer rounded px-2 py-1 text-left caption-2-medium text-label-normal break-all hover:bg-neutral-800/8"
          >
            {describeLocation(location)}
          </button>
        ))}
      </div>
    </div>
  );
};
